import axios from '@/plugins/axios';
import qs from 'qs';
import utils from '@/stores/utils';
import customerorders from "./submodules/customerorders";
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

/** Shared across all commission tabs (section constraints applied at query time). */
const COMMISSIONS_FILTERS_STORAGE_KEY = getStorageKey('commissions');

/** Always-on query flags — never persist as user prefs (tab overlay owns them). */
const COMMISSIONS_BASE_CONSTRAINT_KEYS = [
    'exclude_readonly',
    'providerorder',
    'suborder',
    'exclude_canceled',
    'exclude_ignored_commissions',
];

/**
 * Drop always-on constraints and empty status arrays left by the old per-tab
 * storage (empty `agent_commission_status` matches nothing on the API).
 */
function sanitizeCommissionsUserFilters(filters) {
    const next = { ...(filters || {}) };
    for (const key of COMMISSIONS_BASE_CONSTRAINT_KEYS) {
        delete next[key];
    }
    if (
        Array.isArray(next.agent_commission_status) &&
        next.agent_commission_status.length === 0
    ) {
        delete next.agent_commission_status;
    }
    return next;
}

const COMMISSIONS_STORAGE_OPTIONS = {
    storageKey: COMMISSIONS_FILTERS_STORAGE_KEY,
    fieldsToSave: null,
    fieldsToExclude: ['assigned_to', 'parent_assigned_to'],
    sectionOverlay: true,
    sanitizeUserFilters: sanitizeCommissionsUserFilters,
};

/** Session counter key for a commissions tab — same keys as tab badges / placeholders. */
const SECTION_STATS_KEYS = {
    all: 'commissioned_customerorders',
    // waiting tab includes ready (no separate ready tab in the UI).
    waiting: 'waiting_commissioned_customerorders',
    ready: 'waiting_commissioned_customerorders',
    billed: 'billed_commissioned_customerorders',
    paid: 'paid_commissioned_customerorders',
};

const EMPTY_ORDERS_STATS = {
    count: 0,
    orders_without_margin: 0,
    orders_unassigned: 0,
    total_amount_excl_vat_sum: 0,
    total_amount_incl_vat_sum: 0,
    total_agent_commission_sum: 0,
    total_margin_excl_vat_sum: 0,
    average_basket_excl_vat: null,
    average_order_value_excl_vat: null,
    total_revenue_sum: 0,
    revenue_ratio: null,
    total_agent_commission_ratio: null,
    total_margin_ratio: null,
    total_items: 0,
};

const getSectionCommissionsCount = (stats, section) => {
    if (!stats) {
        return null;
    }
    const key = SECTION_STATS_KEYS[section];
    if (!key) {
        return null;
    }
    const count = stats[key];
    return count == null ? null : count;
};

const isCommissionsSectionKnownEmpty = (rootState, section) => {
    if (!rootState.session?.stats_loaded) {
        return false;
    }
    return getSectionCommissionsCount(rootState.session.stats, section) === 0;
};

const applyEmptyCommissionsOrdersState = (commit, state) => {
    const ordersState = state.customerorders;
    if (ordersState?.orders_cancel_source) {
        ordersState.orders_cancel_source.cancel('canceled');
        commit('customerorders/updateOrdersCancelSource', null);
    }
    if (ordersState?.orders_cancel_source_stats) {
        ordersState.orders_cancel_source_stats.cancel('canceled');
        commit('customerorders/updateOrdersCancelSourceStats', null);
    }
    commit('customerorders/updateOrders', []);
    commit('customerorders/updateOrdersCount', 0);
    commit('customerorders/updateOrdersLoading', false);
    commit('customerorders/updateOrdersLoaded', true);
    commit('customerorders/updateOrdersLoadingErrors', null);
    commit('customerorders/updateOrdersStats', { ...EMPTY_ORDERS_STATS });
    commit('customerorders/updateOrdersStatsLoading', false);
};

const state = {
    section: null,
    selected_items: [],
    bulk_action_loading: false,
};

/**
 * Hard tab constraints only. agent_commission_status is omitted on "all" so the
 * user filter remains available; other tabs force it at query time.
 * Waiting covers WAITING + READY (no dedicated ready tab).
 */
function getSectionFilters(section) {
    const filters = {
        exclude_readonly: false,
        providerorder: true,
        suborder: true,
        exclude_canceled: true,
        exclude_ignored_commissions: true,
    };
    switch (section) {
        case 'ready':
            filters.agent_commission_status = ['ready'];
            break;
        case 'waiting':
            filters.agent_commission_status = ['waiting', 'ready'];
            break;
        case 'billed':
            filters.agent_commission_status = ['billed'];
            break;
        case 'paid':
            filters.agent_commission_status = ['paid'];
            break;
        default:
            break;
    }
    return filters;
}

/** One-shot migrate from legacy per-section storage keys to the shared key. */
function ensureCommissionsFiltersMigrated() {
    const shared = loadFiltersFromStorage(COMMISSIONS_FILTERS_STORAGE_KEY, null);
    if (shared) {
        return;
    }
    const legacySections = ['all', 'ready', 'waiting', 'billed', 'paid'];
    for (const section of legacySections) {
        const legacy = loadFiltersFromStorage(
            getStorageKey('commissions', section),
            null,
        );
        if (legacy) {
            saveFiltersToStorage(
                COMMISSIONS_FILTERS_STORAGE_KEY,
                sanitizeCommissionsUserFilters(legacy),
            );
            return;
        }
    }
}

const mutations = {
    updateSection(state, section) {
        state.section = section;
    },
    updateSelectedItems(state, items) {
        state.selected_items = items;
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },
};

const actions = {

    exportOrdersAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.customerorders.orders_filters);
            filters.offset = null;
        } else {
            filters = {
                ...getSectionFilters('all'),
                limit: 20,
                ordering: '-order_id',
            };
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;

        $utils.downloadFile({
            url: "/api/customerorders/",
            params: filters,
        }, $utils.getFilename('commissions'));
    },

    exportOrderItemsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            for (let key in state.customerorders.orders_filters) {
                filters['order_' + key] = state.customerorders.orders_filters[key];
            }
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;

        $utils.downloadFile({
            url: "/api/customerorderitems/",
            params: filters,
        }, $utils.getFilename('commissionItems'));
    },

    async assignOrder({ commit, dispatch, state }, params) {
        const url = `/api/customerorders/${params.order.id}/assign/`;

        try {
            const response = await axios.put(url, { user: params.user });
            dispatch('refreshCommissionsPage');
            dispatch('session/fetchStats', null, { root: true });
            return response;
        } catch (xhr_error) {
            return Promise.reject(utils.handleError(xhr_error));
        }
    },

    async init({ dispatch, state }) {
        ensureCommissionsFiltersMigrated();
        await dispatch('customerorders/applySectionFilters', {
            storageOptions: COMMISSIONS_STORAGE_OPTIONS,
            filters: getSectionFilters(state.section || 'all'),
            resetOffset: true,
        });
        await dispatch('refreshCommissionsPage');
    },

    async changeSection({ commit, dispatch, state }, section) {
        ensureCommissionsFiltersMigrated();
        const sectionChanged = section != state.section;
        if (sectionChanged) {
            commit('updateSection', section);
        }
        if (sectionChanged || !state.customerorders.orders_user_filters_hydrated) {
            // Keep user filters in memory; only swap the tab overlay (same pattern
            // as customerorders / providerorders). Do not re-run full submodule init.
            await dispatch('customerorders/applySectionFilters', {
                storageOptions: COMMISSIONS_STORAGE_OPTIONS,
                filters: getSectionFilters(section),
                resetOffset: sectionChanged,
            });
        }
        await dispatch('refreshCommissionsPage');
        dispatch('session/fetchStats', null, { root: true });
    },

    /**
     * Skip list/stats fetches when session counters already prove the tab is empty
     * (same pattern as customerorders / customerinvoices).
     */
    async refreshCommissionsPage({ commit, dispatch, state, rootState }) {
        if (isCommissionsSectionKnownEmpty(rootState, state.section || 'all')) {
            applyEmptyCommissionsOrdersState(commit, state);
            return;
        }
        await dispatch('customerorders/fetchOrders');
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/customerorders/bulk/",
                action: params.action,
                instances: state.selected_items.map((item) => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            dispatch('refreshCommissionsPage');
            dispatch('session/fetchStats', null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    modules: {
        customerorders: customerorders(COMMISSIONS_STORAGE_OPTIONS),
    }
};
